import React from 'react';
import { Sparkles, ShieldAlert, CheckCircle, AlertCircle, ArrowRight } from 'lucide-react';
import { ReconciliationSummary, ReconciledItem } from '../../types/reconciliation';
import { generateDailyExecutiveSummary } from '../../engine/aiEngine';

interface AiSummaryCardProps {
  summary: ReconciliationSummary;
  items: ReconciledItem[];
  onExploreExceptions?: () => void;
}

export const AiSummaryCard: React.FC<AiSummaryCardProps> = ({
  summary,
  items,
  onExploreExceptions,
}) => {
  const briefing = generateDailyExecutiveSummary(summary, items);

  const getHealthBadge = () => {
    switch (briefing.healthScore) {
      case 'Optimal':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/80 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800">
            <CheckCircle className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
            Optimal Reconciliation Health
          </span>
        );
      case 'Attention Required':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-950/80 dark:text-amber-300 border border-amber-300 dark:border-amber-800">
            <AlertCircle className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
            Audit Attention Required
          </span>
        );
      case 'Critical Review':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-rose-100 text-rose-800 dark:bg-rose-950/80 dark:text-rose-300 border border-rose-300 dark:border-rose-800">
            <ShieldAlert className="w-3.5 h-3.5 text-rose-600 dark:text-rose-400" />
            High Variance Detected
          </span>
        );
    }
  };

  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-slate-850 to-brand-950 text-white p-6 shadow-xl border border-slate-700/60">
      {/* Background ambient glow */}
      <div className="absolute top-0 right-0 -mt-8 -mr-8 w-64 h-64 bg-brand-500/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-slate-700/60">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-gradient-to-tr from-brand-600 to-sky-400 text-white shadow-md">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-base tracking-tight text-white">
                  AI Reconciliation Intelligence Briefing
                </h3>
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-brand-500/20 text-brand-300 font-mono font-semibold border border-brand-500/30">
                  Automated Synthesis
                </span>
              </div>
              <p className="text-xs text-slate-300">
                {briefing.headline}
              </p>
            </div>
          </div>
          <div>{getHealthBadge()}</div>
        </div>

        {/* Key Analysis Points */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-5">
          <div className="space-y-2.5">
            <div className="text-xs font-semibold text-brand-300 uppercase tracking-wider">
              Ledger Concordance Insights
            </div>
            <ul className="space-y-2 text-xs text-slate-200">
              {briefing.bulletPoints.slice(0, 3).map((pt, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-400 mt-1.5 flex-shrink-0" />
                  <span>{pt}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="space-y-2.5">
            <div className="text-xs font-semibold text-amber-300 uppercase tracking-wider">
              Discrepancy & Risk Remediation
            </div>
            <ul className="space-y-2 text-xs text-slate-200">
              {briefing.bulletPoints.slice(3).map((pt, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 flex-shrink-0" />
                  <span>{pt}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Executive Risk Recommendation Footer */}
        <div className="pt-4 border-t border-slate-700/60 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
          <div className="text-slate-300 italic">
            <span className="font-semibold text-white not-italic">Recommendation: </span>
            {briefing.riskAnalysis}
          </div>
          {onExploreExceptions && (
            <button
              onClick={onExploreExceptions}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-500 text-white font-medium text-xs shadow-sm transition flex-shrink-0"
            >
              <span>Examine Exception Records</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
